import React, { useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, CalendarDays, ChevronDown, ExternalLink, Eye, List, Loader2, PlayCircle } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { API_BASE, LULLABY_CHANNEL_LINK } from "@/config/links";
import { trackClick } from "@/lib/track";
import { recordView } from "@/lib/views";
import { useSeo, SITE_URL } from "@/lib/seo";

function formatViews(value) {
  return Number(value || 0).toLocaleString("ru-RU");
}

function formatDate(value) {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function shortDate(value) {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
  });
}

function monthKey(value) {
  return /^\d{4}-\d{2}/.test(value || "") ? value.slice(0, 7) : "unknown";
}

function monthLabel(key) {
  if (key === "unknown") return "Без даты";
  const [year, month] = key.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("ru-RU", {
    month: "long",
    year: "numeric",
  });
}

// fetch при недоступном API кидает TypeError "Failed to fetch" — латиницу пользователю не показываем.
function friendlyError(error, fallback) {
  const message = error?.message || "";
  return /[А-Яа-яЁё]/.test(message) ? message : fallback;
}

function cleanLine(line) {
  return line.replace(/^#+\s*/, "").replace(/^["'«]+|["'»]+$/g, "").trim();
}

function lullabyParagraphs(lullaby) {
  if (!lullaby?.text) return [];
  return lullaby.text.split(/\r?\n/).map(cleanLine).filter(Boolean);
}

function groupLullabiesByMonth(lullabies) {
  const groups = new Map();
  for (const lullaby of lullabies) {
    const key = monthKey(lullaby.date);
    if (!groups.has(key)) groups.set(key, { key, label: monthLabel(key), items: [] });
    groups.get(key).items.push(lullaby);
  }
  return [...groups.values()];
}

const LullabyPage = () => {
  const { sourceKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = React.useState({ loading: true, error: "", lullaby: null, lullabies: [] });

  useEffect(() => {
    let alive = true;
    async function loadLullaby() {
      try {
        const lullabyResponse = await fetch(`${API_BASE}/api/lullabies/${encodeURIComponent(sourceKey)}`);
        if (lullabyResponse.status === 404) throw new Error("Эта колыбельная не найдена");
        if (!lullabyResponse.ok) throw new Error("Не удалось загрузить колыбельную");
        const lullabyData = await lullabyResponse.json();

        let lullabies = [];
        try {
          const archiveResponse = await fetch(`${API_BASE}/api/lullabies?limit=500`);
          if (archiveResponse.ok) {
            const archiveData = await archiveResponse.json();
            lullabies = archiveData.lullabies || [];
          }
        } catch {
          lullabies = [];
        }

        if (alive) {
          setState({
            loading: false,
            error: "",
            lullaby: lullabyData.lullaby || null,
            lullabies,
          });
        }
      } catch (error) {
        if (alive) {
          setState({
            loading: false,
            error: friendlyError(error, "Колыбельная временно недоступна"),
            lullaby: null,
            lullabies: [],
          });
        }
      }
    }
    loadLullaby();
    return () => {
      alive = false;
    };
  }, [sourceKey]);

  useEffect(() => {
    if (state.lullaby?.hasVideo) recordView("lullabies", sourceKey);
  }, [state.lullaby?.hasVideo, sourceKey]);

  const lullaby = state.lullaby;
  useSeo({
    title: lullaby ? `${lullaby.title} — колыбельная | Сказки детям на ночь` : undefined,
    description: lullaby?.excerpt,
    path: `/lullabies/${sourceKey}`,
    image: lullaby?.coverUrl || undefined,
    jsonLd: lullaby
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: lullaby.title,
          description: lullaby.excerpt,
          image: lullaby.coverUrl || undefined,
          datePublished: lullaby.createdAt || undefined,
          url: `${SITE_URL}/lullabies/${sourceKey}`,
          inLanguage: "ru",
          isFamilyFriendly: true,
          author: { "@type": "Organization", name: "Сказки детям на ночь" },
          publisher: { "@type": "Organization", name: "Сказки детям на ночь" },
        }
      : undefined,
  });

  const paragraphs = useMemo(() => lullabyParagraphs(state.lullaby), [state.lullaby]);
  const archiveLullabies = useMemo(() => {
    if (!state.lullaby) return state.lullabies;
    const exists = state.lullabies.some((lullaby) => lullaby.sourceKey === state.lullaby.sourceKey);
    return exists ? state.lullabies : [state.lullaby, ...state.lullabies];
  }, [state.lullaby, state.lullabies]);
  const lullabyGroups = useMemo(() => groupLullabiesByMonth(archiveLullabies), [archiveLullabies]);
  const activeGroupKey = monthKey(state.lullaby?.date);
  const nextLullaby = useMemo(() => {
    const index = archiveLullabies.findIndex((lullaby) => lullaby.sourceKey === sourceKey);
    if (index < 0) return null;
    return archiveLullabies[index + 1] || null;
  }, [archiveLullabies, sourceKey]);
  const shouldAutoplay = Boolean(location.state?.autoplay);

  function handleVideoEnded() {
    if (!nextLullaby) return;
    navigate(`/lullabies/${nextLullaby.sourceKey}`, { state: { autoplay: true } });
  }

  return (
    <div className="min-h-screen bg-night-900 text-cream">
      <Header />
      <main>
        {state.loading ? (
          <section className="flex min-h-[60vh] items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Загружаю колыбельную
          </section>
        ) : state.error || !state.lullaby ? (
          <section className="py-14">
            <div className="mx-auto max-w-3xl px-4 sm:px-6">
              <Link to="/lullabies" className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-cream transition-colors">
                <ArrowLeft className="h-4 w-4" />
                Все колыбельные
              </Link>
              <div className="rounded-2xl border border-amber/30 bg-amber/10 px-5 py-5 text-amber-soft">{state.error || "Колыбельная не найдена"}</div>
            </div>
          </section>
        ) : (
          <>
            <section className="border-b border-line">
              <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 md:py-10">
                <div className="mb-6 flex flex-wrap items-center gap-3">
                  <Link to="/lullabies" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-cream transition-colors">
                    <ArrowLeft className="h-4 w-4" />
                    Все колыбельные
                  </Link>
                  <span className="text-line-strong">/</span>
                  <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                    <CalendarDays className="h-4 w-4" />
                    {formatDate(state.lullaby.date)}
                  </span>
                  {state.lullaby.rubric ? (
                    <>
                      <span className="text-line-strong">/</span>
                      <span className="text-sm text-amber-soft">{state.lullaby.rubric}</span>
                    </>
                  ) : null}
                  {state.lullaby.hasVideo ? (
                    <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                      <Eye className="h-4 w-4" />
                      {formatViews(state.lullaby.views)}
                    </span>
                  ) : null}
                </div>

                <div className="max-w-3xl">
                  <h1 className="font-serif text-4xl leading-tight text-cream sm:text-5xl">{state.lullaby.title}</h1>
                  {state.lullaby.theme ? <p className="mt-4 text-base leading-7 text-muted-foreground">{state.lullaby.theme}</p> : null}
                </div>
              </div>
            </section>

            <section className="py-8 md:py-10">
              <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[300px_minmax(0,1fr)]">
                <aside className="order-2 lg:order-1 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:self-start lg:overflow-y-auto">
                  <nav className="rounded-2xl border border-line bg-night-card p-4" aria-label="Другие колыбельные">
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 text-sm font-semibold text-cream">
                          <List className="h-4 w-4" />
                          Другие колыбельные
                        </div>
                        <div className="mt-1 text-xs text-dim">{archiveLullabies.length} колыбельных</div>
                      </div>
                      <Link to="/lullabies" className="rounded-full border border-line-strong px-2 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:border-amber hover:text-cream">
                        Все колыбельные
                      </Link>
                    </div>

                    <div className="mb-5 grid grid-cols-2 gap-2 border-b border-line pb-4">
                      <a href="#lullaby-video" className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-line-strong bg-night-900 px-3 text-sm font-medium text-muted-foreground transition-colors hover:border-amber hover:text-cream">
                        <PlayCircle className="h-4 w-4" />
                        Видео
                      </a>
                      <a href="#lullaby-text" className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-line-strong bg-night-900 px-3 text-sm font-medium text-muted-foreground transition-colors hover:border-amber hover:text-cream">
                        <BookOpen className="h-4 w-4" />
                        Текст
                      </a>
                    </div>

                    <div className="space-y-2">
                      {lullabyGroups.map((group) => (
                        <details key={group.key} className="group" open={group.key === activeGroupKey || lullabyGroups.length === 1}>
                          <summary className="flex min-h-10 cursor-pointer list-none items-center justify-between gap-3 rounded-lg px-2 text-sm font-semibold text-cream transition-colors hover:bg-night-700 [&::-webkit-details-marker]:hidden">
                            <span className="capitalize">{group.label}</span>
                            <span className="inline-flex items-center gap-2 text-xs font-medium text-dim">
                              {group.items.length}
                              <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                            </span>
                          </summary>
                          <div className="mt-1 space-y-1 border-l border-line pl-3">
                            {group.items.map((lullaby) => {
                              const active = lullaby.sourceKey === sourceKey;
                              return (
                                <Link
                                  key={lullaby.sourceKey}
                                  to={`/lullabies/${lullaby.sourceKey}`}
                                  className={`block rounded-lg px-3 py-2 text-sm leading-5 transition-colors ${
                                    active
                                      ? "bg-amber/15 font-semibold text-amber-soft"
                                      : "text-muted-foreground hover:bg-night-700 hover:text-cream"
                                  }`}
                                >
                                  <span className="mb-1 block text-xs font-medium text-dim">{shortDate(lullaby.date)}</span>
                                  <span>{lullaby.title}</span>
                                </Link>
                              );
                            })}
                          </div>
                        </details>
                      ))}
                    </div>
                  </nav>
                </aside>

                <div className="order-1 lg:order-2 min-w-0">
                  <section id="lullaby-video" className="scroll-mt-24">
                    {state.lullaby.videoUrl ? (
                      <div className="overflow-hidden rounded-2xl border border-line-strong bg-black shadow-[0_16px_48px_-16px_rgba(0,0,0,0.7)]">
                        <video
                          key={state.lullaby.sourceKey}
                          className="aspect-video w-full bg-black"
                          src={state.lullaby.videoUrl}
                          poster={state.lullaby.coverUrl || undefined}
                          controls
                          preload="metadata"
                          autoPlay={shouldAutoplay}
                          onEnded={handleVideoEnded}
                        />
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-amber/25 bg-amber/10 px-5 py-4">
                        <div className="flex items-center gap-2 font-semibold text-amber-soft">
                          <PlayCircle className="h-5 w-5" />
                          Видео этой колыбельной — в MAX-канале
                        </div>
                        <a
                          href={LULLABY_CHANNEL_LINK}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackClick("lullaby_channel")}
                          className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full bg-amber hover:bg-amber-hover px-4 text-sm font-semibold text-night-900 transition-colors"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Смотреть в канале
                        </a>
                      </div>
                    )}
                    {nextLullaby ? (
                      <div className="mt-3 text-sm text-dim">
                        После окончания откроется следующая: <span className="text-muted-foreground">{nextLullaby.title}</span>
                      </div>
                    ) : null}
                  </section>

                  <article id="lullaby-text" className="scroll-mt-24 pt-10">
                    <div className="mb-5 flex items-center gap-2 text-sm font-semibold text-cream">
                      <BookOpen className="h-4 w-4" />
                      Текст колыбельной
                    </div>
                    <div className="reading-column">
                      {paragraphs.map((paragraph, index) => (
                        <p key={`${state.lullaby.sourceKey}-${index}`}>{paragraph}</p>
                      ))}
                    </div>
                  </article>

                  <div className="mt-10 flex flex-wrap gap-3 border-t border-line pt-6">
                    <a
                      href={LULLABY_CHANNEL_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackClick("lullaby_channel")}
                      className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-amber hover:bg-amber-hover px-5 text-sm font-semibold text-night-900 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Смотреть в канале
                    </a>
                    <Link
                      to="/lullabies"
                      className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-line-strong px-5 text-sm font-semibold text-muted-foreground transition-colors hover:border-amber hover:text-cream"
                    >
                      <BookOpen className="h-4 w-4" />
                      Все колыбельные
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default LullabyPage;
